import { fromType } from "@src/store/helpers";
import { getThunkAndSlice } from "@src/store/getThunkAndSlice";
import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "@src/store/store";
import { _selectMapSplitMinQuantity, _selectSelectedRowNames, _selectSelectedSectionNames } from "@src/store/map/mapSlice";
import { matchesFilterTokens } from "@src/utils/sectionFilter.utils";
import { TGametimeListingError } from "@src/components/utils/gametimeListingStatus";

export interface GameTimeListing {
	listingId: number;
	isShared: boolean;
	section: string | null;
	row: string | null;
	quantity: number | null;
	itemPrice: number | null;
	gametimePrice: number | null;
	companyName: string | null;
	status: string | null;
	gametimeListingId: string | null;
	gametimeEventId: string | null;
	errors: Array<TGametimeListingError>;
	statusRefreshedDate: string | null;
	lastSuccessfulSyncDate: string | null;
	deletedDate: string | null;
	syncStatus?: number | null;
	syncMessage?: string | null;
}

export interface GameTimeListingsResponse {
	eventId: number;
	refreshedAtUtc: string | null;
	listings: Array<GameTimeListing>;
}

const initialState = fromType<GameTimeListingsResponse>({ eventId: 0, refreshedAtUtc: null, listings: [] });

export const { thunk: getGametimeListings, slice: getGametimeListingsSlice } = getThunkAndSlice<
	{ eventId: number },
	typeof initialState
>({
	initialState,
	path: ({ eventId }) => `/api/Marketplace/GameTime/Event/${eventId}/Listings`,
	cacheId: "eventId",
});

const selectGametimeListingsData = (state: RootState) => state.marketplace.gameTime.activeListings;

export const selectAggregatedGametimeListings = createSelector(
	[selectGametimeListingsData, _selectSelectedSectionNames, _selectMapSplitMinQuantity, _selectSelectedRowNames],
	(listingsData, selectedSections, minQuantity, selectedRows) => {
		const filtered =
			listingsData.data?.listings?.filter((x) => {
				const isSectionIncluded = selectedSections.length > 0 ? matchesFilterTokens(selectedSections, x.section ?? "") : true;
				const isRowIncluded = selectedRows.length > 0 ? matchesFilterTokens(selectedRows, x.row ?? "") : true;
				const isMinQuantityIncluded = !minQuantity || (x.quantity ?? 0) >= +minQuantity;
				return isSectionIncluded && isRowIncluded && isMinQuantityIncluded;
			}) ?? [];

		return {
			gametimeListingsLoading: listingsData.loading,
			gametimeListings: filtered,
			gametimeListingsRefreshedAt: listingsData.data?.refreshedAtUtc ?? null,
		};
	},
);
